import type { SectionAssignment } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { InlineDialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { useRootStore } from 'hooks/rootStore'
import { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { useId } from 'react'

interface SectionEndDateConflictDialogProps {
  open: boolean
  onClose: () => void
  conflictingAssignments: SectionAssignment[]
  proposedEndDate: DateTime
  sectionId: string
}

export const SectionEndDateConflictDialog = observer(
  function SectionEndDateConflictDialog({
    open,
    onClose,
    conflictingAssignments,
    proposedEndDate,
    sectionId,
  }: SectionEndDateConflictDialogProps) {
    const { tt } = useTranslationTyped()
    const rootStore = useRootStore()
    const headingId = useId()

    const handleAssignmentClick = (assignmentId: string) => {
      rootStore.navigateToInstructorAssignmentPage(sectionId, assignmentId, {
        tab: 'students',
      })
      onClose()
    }

    if (!open) return null

    return (
      <InlineDialog
        open={open}
        size="lg"
        className="flex flex-col items-center"
        innerClassName="flex flex-col w-[600px] max-h-[80vh]"
        aria-labelledby={headingId}
      >
        <DialogCloseButton
          onClick={() => {
            onClose()
            return false
          }}
        />

        <h1
          id={headingId}
          className="mb-4 text-center text-headline-large text-core-on-tertiary"
        >
          {tt.instructor_classes.section_end_date_conflict_title()}
        </h1>

        <div className="mb-6 text-center text-body-medium text-on-surface-var">
          {tt.instructor_classes.section_end_date_conflict_description({
            endDate: proposedEndDate.toLocaleString(DateTime.DATE_MED),
            count: conflictingAssignments.length,
          })}
        </div>

        <div className="mb-6 w-full">
          <div className="mb-3 text-body-medium font-medium text-on-surface">
            {tt.instructor_classes.conflicting_assignments()}:
          </div>

          <div className="flex flex-col gap-3 overflow-y-auto rounded-lg border border-outline-medium bg-surface-bright p-4">
            {conflictingAssignments.map((assignment) => (
              <div
                key={assignment.id}
                className="flex w-full cursor-pointer items-center justify-between rounded-md border border-outline-light bg-surface p-3 hover:bg-surface-dim"
                onClick={() => handleAssignmentClick(assignment.id)}
              >
                {assignment.slideDeck.data.slideDeckImageURL && (
                  <img
                    src={assignment.slideDeck.data.slideDeckImageURL}
                    className="max-h-12 max-w-12 p-1"
                    alt=""
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-body-medium font-medium text-on-surface">
                    {assignment.slideDeck?.data?.slideDeckName || 'Assignment'}
                  </div>
                  <div className="text-body-small text-on-surface-var">
                    {tt.instructor_classes.expires_on()}:{' '}
                    {assignment.formattedExpiresAt}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <BreakoutButton
          kind="secondary"
          size="large"
          fullWidth
          onClick={onClose}
        >
          {tt.common.close()}
        </BreakoutButton>
      </InlineDialog>
    )
  }
)
